<script setup>
import { useRoute, useRouter } from "vue-router";
import { ref, computed, watch } from "vue";
import { useStore } from "vuex";
import SidenavCollapse from "./SidenavCollapse.vue";
import { 
  isGuest, 
  isStudent, 
  isInstructor, 
  isAdmin,
  isOwner,
  isAdminOrOwner 
} from "@/utilities/roles";

const route = useRoute();
const router = useRouter();
const store = useStore();

// Get reactive user data from store
const userData = computed(() => store.state.userData);
const currentRoleId = computed(() => userData.value?.roleId);

// Admin/Owner should see all items
const showAllItems = computed(() => isAdminOrOwner(currentRoleId.value));

// Reactive role checks
const showStudentItems = computed(() => showAllItems.value || isStudent(currentRoleId.value));
const showInstructorItems = computed(() => showAllItems.value || isInstructor(currentRoleId.value));
const showAdminItems = computed(() => isAdminOrOwner(currentRoleId.value));

const isActive = (path) => {
  return computed(() => route.path === path).value;
};

// Watch for role changes and reset navigation if needed
watch(currentRoleId, (newRole, oldRole) => {
  if (newRole !== oldRole) {
    // Redirect to appropriate page if current route is not accessible
    if (
      (route.path.startsWith('/cursos/mis-cursos') && !showStudentItems.value) ||
      (route.path.startsWith('/cursos') && !showInstructorItems.value) ||
      (route.path.startsWith('/usuarios') && !showAdminItems.value)
    ) {
      router.push('/');
    }
  }
});
</script>

<template>
  <div id="sidenav-collapse-main" class="collapse navbar-collapse w-auto h-auto h-100 overflow-hidden">
    <ul class="navbar-nav">
      <!-- For all logged-in users -->
      <li class="nav-item">
        <sidenav-collapse nav-text="Explorar Cursos" :collapse="false" url="#" :aria-controls="''"
          collapse-ref="/" :class="{'active-item': isActive('/')}">
          <template #icon>
            <i class="fas fa-search text-lg pb-1"></i>
          </template>
        </sidenav-collapse>
        
        <!-- For students and admin/owner -->
        <sidenav-collapse v-if="showStudentItems" nav-text="Mis Cursos" :collapse="false" url="#" :aria-controls="''"
          collapse-ref="/cursos/mis-cursos" :class="{'active-item': isActive('/cursos/mis-cursos')}">
          <template #icon>
            <i class="fas fa-chalkboard text-lg pb-1"></i>
          </template>
        </sidenav-collapse>    
           
      </li>

      <hr v-if="showAdminItems" class="mt-3 horizontal light" />

      <!-- For instructors and admin/owner -->
      <li class="nav-item" v-if="showInstructorItems">
        <sidenav-collapse nav-text="Cursos" :collapse="false" url="#" :aria-controls="''" collapse-ref="/cursos"
          :class="{'active-item': isActive('/cursos')}">
          <template #icon>
            <i class="fas fa-chalkboard-teacher text-lg pb-1"></i>
          </template>
        </sidenav-collapse>

        <sidenav-collapse v-if="!showAdminItems" nav-text="Calificaciones" :collapse="false" url="#" :aria-controls="''"
          collapse-ref="/calificaciones" :class="{'active-item': isActive('/calificaciones')}">
          <template #icon>
            <i class="fas fa-spell-check text-lg pb-1"></i>
          </template>
        </sidenav-collapse>

        <!--
        <sidenav-collapse nav-text="Analiticas" :collapse="false" url="#" :aria-controls="''"
          collapse-ref="/analiticas" :class="{'active-item': isActive('/analiticas')}">
          <template #icon>
            <i class="fas fa-chart-bar text-lg pb-1"></i>
          </template>
        </sidenav-collapse>-->
      </li>

      <hr v-if="showInstructorItems" class="mt-3 horizontal light" />

      <!-- Only for admin/owner -->
      <li class="nav-item" v-if="showAdminItems">
        <sidenav-collapse nav-text="Usuarios" :collapse="false" url="#" :aria-controls="''" collapse-ref="/usuarios"
          :class="{'active-item': isActive('/usuarios')}">
          <template #icon>
            <i class="fas fa-users text-lg pb-1"></i>
          </template>
        </sidenav-collapse>    
        <sidenav-collapse nav-text="Categorias" :collapse="false" url="#" :aria-controls="''" collapse-ref="/categorias"
          :class="{'active-item': isActive('/categorias')}">
          <template #icon>
            <i class="fas fa-tags text-lg pb-1"></i>
          </template>
        </sidenav-collapse>    
      </li>    
    </ul>
  </div>
</template>

<style >
.navbar-vertical .navbar-nav .nav-item .nav-link{
  color: #ffffff !important;
  font-weight: 500 !important;
}
.navbar-vertical .navbar-nav .nav-item .nav-link.active-item {
  background-color: #e3e3e998 !important;
  border-radius: 5px;
  font-weight: 600 !important;
  /* color: rgb(255, 255, 255) !important; */
}

.navbar-vertical .navbar-nav .nav-link {
  color: #ffffff !important;
}
.navbar-vertical.navbar-expand-xs {
  overflow-y: auto !important;
}
</style>